export type StaffPermissionKey =
  | "view_own_bookings"
  | "view_all_bookings"
  | "manage_bookings"
  | "block_own_agenda"
  | "view_client_records"
  | "view_client_contacts"
  | "use_pdv"
  | "view_finances"
  | "apply_discounts"
  | "manage_services"
  | "manage_products"
  | "manage_content";

export interface StaffPermissionDef {
  key: StaffPermissionKey;
  label: string;
  hint: string;
  group: string;
}

export const STAFF_PERMISSIONS: StaffPermissionDef[] = [
  { key: "view_own_bookings", label: "Ver as suas reservas", hint: "Acesso à agenda pessoal do colaborador.", group: "Agenda" },
  { key: "view_all_bookings", label: "Ver reservas de toda a equipa", hint: "Vê também as reservas atribuídas a outros colaboradores.", group: "Agenda" },
  { key: "manage_bookings", label: "Alterar estado das reservas", hint: "Confirmar, recusar ou concluir reservas.", group: "Agenda" },
  { key: "block_own_agenda", label: "Bloquear horários próprios", hint: "Marcar indisponibilidade na sua agenda.", group: "Agenda" },
  { key: "view_client_records", label: "Ver fichas de cliente", hint: "Consultar a ficha de avaliação e histórico de sessões.", group: "Clientes" },
  { key: "view_client_contacts", label: "Ver contactos do cliente", hint: "Email, telemóvel e morada do cliente.", group: "Clientes" },
  { key: "use_pdv", label: "Utilizar o Caixa (PDV)", hint: "Abrir e operar sessões de caixa.", group: "Vendas" },
  { key: "apply_discounts", label: "Aplicar descontos", hint: "Conceder desconto em reservas e vendas.", group: "Vendas" },
  { key: "view_finances", label: "Ver financeiro", hint: "Relatórios, receitas e despesas.", group: "Gestão" },
  { key: "manage_services", label: "Gerir serviços e pacotes", hint: "Criar e editar serviços.", group: "Gestão" },
  { key: "manage_products", label: "Gerir produtos e stock", hint: "Criar e editar produtos da loja.", group: "Gestão" },
  { key: "manage_content", label: "Editar conteúdo do site", hint: "Textos e imagens das páginas públicas.", group: "Gestão" },
];

export type StaffAccessRole = "admin" | "gerente" | "financeiro" | "caixa" | "colaborador";

/** Categoria com acesso total ao painel admin. */
export const ADMIN_ROLE_KEY = "admin";

export const ALL_PERMISSION_KEYS: StaffPermissionKey[] = [
  "view_own_bookings",
  "view_all_bookings",
  "manage_bookings",
  "block_own_agenda",
  "view_client_records",
  "view_client_contacts",
  "use_pdv",
  "apply_discounts",
  "view_finances",
  "manage_services",
  "manage_products",
  "manage_content",
];

export const STAFF_ROLE_PRESETS: Record<
  StaffAccessRole,
  { label: string; description: string; permissions: StaffPermissionKey[] }
> = {
  admin: {
    label: "Administrador",
    description: "Acesso total ao painel admin: todos os menus, definições, financeiro, PDV e gestão.",
    permissions: [...ALL_PERMISSION_KEYS],
  },
  gerente: {
    label: "Gerente",
    description: "Acesso quase total: agenda de toda a equipa, clientes, caixa, financeiro e gestão.",
    permissions: [
      "view_own_bookings",
      "view_all_bookings",
      "manage_bookings",
      "block_own_agenda",
      "view_client_records",
      "view_client_contacts",
      "use_pdv",
      "apply_discounts",
      "view_finances",
      "manage_services",
      "manage_products",
      "manage_content",
    ],
  },
  financeiro: {
    label: "Financeiro",
    description: "Vê reservas de toda a equipa (sem editar), financeiro e descontos.",
    permissions: ["view_own_bookings", "view_all_bookings", "view_finances", "apply_discounts"],
  },
  caixa: {
    label: "Caixa",
    description: "Opera o PDV e vendas, vê a agenda geral para atendimento.",
    permissions: ["view_own_bookings", "view_all_bookings", "use_pdv", "apply_discounts", "view_client_contacts"],
  },
  colaborador: {
    label: "Colaborador",
    description: "Só vê as suas próprias reservas e pode bloquear a sua agenda.",
    permissions: ["view_own_bookings", "block_own_agenda", "view_client_records"],
  },
};

export const STAFF_ACCESS_STATUS: Record<string, { label: string; className: string }> = {
  pending: { label: "Pendente de aprovação", className: "bg-amber-100 text-amber-800" },
  approved: { label: "Aprovado", className: "bg-emerald-100 text-emerald-800" },
  suspended: { label: "Suspenso", className: "bg-red-100 text-red-800" },
};

export const permissionsFromRole = (role: StaffAccessRole): Record<string, boolean> => {
  const out: Record<string, boolean> = {};
  STAFF_PERMISSIONS.forEach((p) => (out[p.key] = false));
  (STAFF_ROLE_PRESETS[role]?.permissions || []).forEach((p) => (out[p] = true));
  return out;
};

export const hasPerm = (permissions: any, key: StaffPermissionKey) =>
  !!(permissions && permissions[key] === true);

/* ============================================================
   Menus / submenus de acesso (categorias de acesso editáveis)
   ============================================================ */

export interface AccessSubmenu {
  key: StaffPermissionKey;
  label: string;
  hint: string;
}

export interface AccessMenu {
  key: string;
  label: string;
  submenus: AccessSubmenu[];
}

/** Árvore de menus e submenus derivada das permissões existentes. */
export const ACCESS_MENUS: AccessMenu[] = Array.from(
  new Set(STAFF_PERMISSIONS.map((p) => p.group))
).map((group) => ({
  key: group.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, "_"),
  label: group,
  submenus: STAFF_PERMISSIONS.filter((p) => p.group === group).map((p) => ({
    key: p.key,
    label: p.label,
    hint: p.hint,
  })),
}));

/** Categoria de acesso guardada em app_settings (key: access_roles). */
export interface AccessRoleDef {
  key: string;
  label: string;
  description: string;
  permissions: Record<string, boolean>;
  builtin?: boolean;
}

export const slugifyRoleKey = (label: string) =>
  label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || `cat_${Date.now()}`;

export const emptyPermissions = (): Record<string, boolean> => {
  const out: Record<string, boolean> = {};
  STAFF_PERMISSIONS.forEach((p) => (out[p.key] = false));
  return out;
};

/** Presets nativos convertidos para o formato editável. */
export const builtinAccessRoles = (): AccessRoleDef[] =>
  (Object.keys(STAFF_ROLE_PRESETS) as StaffAccessRole[]).map((key) => ({
    key,
    label: STAFF_ROLE_PRESETS[key].label,
    description: STAFF_ROLE_PRESETS[key].description,
    permissions: permissionsFromRole(key),
    builtin: true,
  }));
